import React, { useCallback, useEffect, useRef, useState } from 'react';
import Skeleton from '../components/Skeleton.js';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import VideoPlayer from '../components/VideoPlayer.js';
import LessonCompleteCheckmark from '../components/LessonCompleteCheckmark.js';
import { useToast } from '../hooks/useToast.js';

interface LessonVideo {
  id: string;
  title: string;
  videoUrl: string;
  durationSeconds: number;
  orderIndex: number;
}

interface Lesson {
  id: string;
  title: string;
  description: string;
  notes?: string | null;
  requiresChallenge: boolean;
  videos: LessonVideo[];
}

function formatDuration(seconds: number) {
  const min = Math.floor(seconds / 60);
  const sec = seconds % 60;
  return `${min}:${sec < 10 ? '0' : ''}${sec}`;
}

function VideoStatusIcon({ watchPercentage }: { watchPercentage: number }) {
  if (watchPercentage >= 90) {
    return (
      <span
        className="material-symbols-outlined text-green-500 text-[22px] flex-shrink-0"
        style={{ fontVariationSettings: "'FILL' 1" }}
      >
        check_circle
      </span>
    );
  }
  if (watchPercentage > 0) {
    return (
      <span className="relative w-[22px] h-[22px] flex-shrink-0" title={`${watchPercentage}% watched`}>
        <svg width="22" height="22" viewBox="0 0 22 22" aria-hidden>
          <circle cx="11" cy="11" r="9" fill="none" stroke="#6b7280" strokeWidth="2" />
          <path
            d="M11 11 L11 2 A9 9 0 0 1 20 11 Z"
            fill="#fbbf24"
          />
        </svg>
      </span>
    );
  }
  return (
    <span className="material-symbols-outlined text-outline text-[22px] flex-shrink-0">circle</span>
  );
}

export default function LessonPlayer() {
  const { id: courseId, lessonId } = useParams();
  const navigate = useNavigate();

  const [course, setCourse] = useState<any>(null);
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [lessonsList, setLessonsList] = useState<any[]>([]);
  const [completedLessons, setCompletedLessons] = useState<Set<string>>(new Set());
  const [activeVideoId, setActiveVideoId] = useState<string | null>(null);
  const [videoProgress, setVideoProgress] = useState<Record<string, number>>({});
  const [isCompleting, setIsCompleting] = useState(false);
  const [showCompleteAnim, setShowCompleteAnim] = useState(false);
  const toast = useToast();
  const [xpNotification, setXpNotification] = useState<{ amount: number; title: string } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [challengeHtml, setChallengeHtml] = useState<string | null>(null);
  const [requiresChallenge, setRequiresChallenge] = useState(false);
  const [challengePassed, setChallengePassed] = useState(false);

  const latestWatchRef = useRef(0);
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const sortedVideos = [...(lesson?.videos ?? [])].sort((a, b) => a.orderIndex - b.orderIndex);
  const activeVideo = sortedVideos.find((v) => v.id === activeVideoId) ?? sortedVideos[0] ?? null;

  const updateLocalProgress = useCallback((videoId: string, percent: number) => {
    setVideoProgress((prev) => ({
      ...prev,
      [videoId]: Math.max(prev[videoId] ?? 0, percent)
    }));
  }, []);

  const persistVideoProgress = useCallback(
    async (videoId: string, percent: number) => {
      if (!lessonId) return;
      try {
        await api.post(`/api/lessons/${lessonId}/videos/${videoId}/progress`, {
          watch_percentage: percent
        });
        updateLocalProgress(videoId, percent);
      } catch (err) {
        console.warn('Failed to save video progress:', err);
      }
    },
    [lessonId, updateLocalProgress]
  );

  const flushProgress = useCallback(
    (videoId: string | null) => {
      if (!videoId) return;
      const pct = latestWatchRef.current;
      if (pct > 0) {
        void persistVideoProgress(videoId, pct);
      }
    },
    [persistVideoProgress]
  );

  const selectVideo = useCallback(
    (videoId: string) => {
      if (videoId === activeVideoId) return;
      flushProgress(activeVideoId);
      setVideoProgress((prev) => {
        latestWatchRef.current = prev[videoId] ?? 0;
        return prev;
      });
      setActiveVideoId(videoId);
    },
    [activeVideoId, flushProgress]
  );

  useEffect(() => {
    async function fetchLessonData() {
      if (!courseId || !lessonId) return;
      setIsLoading(true);
      try {
        const [
          courseRes,
          lessonRes,
          progressRes,
          courseProgressRes,
          challengeRes
        ] = await Promise.all([
          api.get(`/api/courses/${courseId}`),
          api.get(`/api/lessons/${lessonId}`),
          api.get(`/api/lessons/${lessonId}/progress`),
          api.get(`/api/courses/${courseId}/progress`),
          api.get(`/api/lessons/${lessonId}/challenge/play`)
        ]);

        setCourse(courseRes.data);
        setLessonsList(courseRes.data?.lessons || []);

        const lessonData = lessonRes.data as Lesson;
        setLesson(lessonData);

        setChallengeHtml(challengeRes.data?.challengeHtml || null);
        setRequiresChallenge(challengeRes.data?.requiresChallenge ?? false);
        setChallengePassed(challengeRes.data?.challengePassed ?? false);

        const videos = [...(lessonData.videos || [])].sort((a, b) => a.orderIndex - b.orderIndex);
        setActiveVideoId(videos[0]?.id ?? null);

        const progressMap: Record<string, number> = {};
        for (const row of progressRes.data || []) {
          progressMap[row.lesson_video_id] = row.watch_percentage;
        }
        setVideoProgress(progressMap);
        if (videos[0]) {
          latestWatchRef.current = progressMap[videos[0].id] ?? 0;
        }

        const completedIds = new Set<string>(
          courseProgressRes.data?.lessons
            ?.filter((l: { completed: boolean }) => l.completed)
            .map((l: { lesson_id: string }) => l.lesson_id) || []
        );
        setCompletedLessons(completedIds);
      } catch (err) {
        console.error('Failed to load lesson:', err);
      } finally {
        setIsLoading(false);
      }
    }

    fetchLessonData();
  }, [courseId, lessonId]);

  // Listen for postMessage from challenge iframe (challenge completed signal)
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      // Accept messages from any origin since challenges are embedded HTML
      if (event.data && event.data.type === 'challenge-passed') {
        setChallengePassed(true);
        toast.push({ type: 'success', message: 'Challenge completed! You can now mark this lesson as complete.' });
      }
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [toast]);

  useEffect(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }

    if (!lessonId || !activeVideoId) return;

    progressIntervalRef.current = setInterval(() => {
      const pct = latestWatchRef.current;
      if (pct > 0) {
        void persistVideoProgress(activeVideoId, pct);
      }
    }, 5000);

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      flushProgress(activeVideoId);
    };
  }, [lessonId, activeVideoId, persistVideoProgress, flushProgress]);

  const handleWatchProgress = (percent: number) => {
    latestWatchRef.current = percent;
    if (activeVideoId) {
      updateLocalProgress(activeVideoId, percent);
    }
  };

  const handleVideoEnded = () => {
    if (!activeVideo) return;
    const idx = sortedVideos.findIndex((v) => v.id === activeVideo.id);
    const next = sortedVideos[idx + 1];
    if (next) {
      flushProgress(activeVideo.id);
      setVideoProgress((prev) => {
        latestWatchRef.current = prev[next.id] ?? 0;
        return prev;
      });
      setActiveVideoId(next.id);
    } else {
      flushProgress(activeVideo.id);
    }
  };

  const allVideosAt90 =
    sortedVideos.length === 0 ||
    sortedVideos.every((v) => (videoProgress[v.id] ?? 0) >= 90);

  const isLessonCompleted = lessonId ? completedLessons.has(lessonId) : false;
  const canMarkComplete = allVideosAt90 && !isLessonCompleted && (!requiresChallenge || challengePassed);

  const handleMarkAsComplete = async () => {
    if (!lessonId || isCompleting || !canMarkComplete) return;
    setIsCompleting(true);

    try {
      flushProgress(activeVideoId);
      const response = await api.post(`/api/lessons/${lessonId}/complete`);
      const { xpAwarded, rankTitle } = response.data;

      setCompletedLessons((prev) => {
        const next = new Set(prev);
        next.add(lessonId);
        return next;
      });

      setShowCompleteAnim(true);
      toast.push({
        type: 'success',
        message:
          xpAwarded > 0
            ? `Lesson complete! +${xpAwarded} XP${rankTitle ? ` · ${rankTitle}` : ''}`
            : 'Lesson complete!'
      });
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { message?: string } } };
      const message =
        axiosErr.response?.data?.message ||
        'Could not mark lesson as complete. Watch all videos to at least 90%.';
      toast.push({ type: 'error', message });
    } finally {
      setIsCompleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex-1 min-h-[60vh] flex items-center justify-center">
        <div className="flex flex-col gap-3 w-full max-w-md px-4">
          <div className="aspect-video rounded-lg overflow-hidden">
            <Skeleton />
          </div>
          <div className="h-8">
            <Skeleton />
          </div>
          <div className="h-4">
            <Skeleton />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-screen bg-background text-on-surface select-none pb-8">
      <LessonCompleteCheckmark
        show={showCompleteAnim}
        onDone={() => setShowCompleteAnim(false)}
      />

      {/* toasts rendered by global ToastProvider */}

      <div className="mb-6 flex items-center justify-between">
        <Link
          to="/courses"
          className="flex items-center gap-2 text-on-surface-variant hover:text-on-surface text-body-sm transition-colors"
        >
          <span className="material-symbols-outlined text-[18px]">arrow_back</span>
          <span>Back to Catalog</span>
        </Link>
        <span className="text-[11px] font-bold uppercase tracking-wider text-on-surface-variant px-3 py-1 bg-surface-container thin-border rounded">
          {course?.title}
        </span>
      </div>

      <div className="grid grid-cols-12 gap-gutter items-start">
        {/* Main column */}
        <div className="col-span-12 lg:col-span-8 space-y-6">
          {lesson && (
            <>
              <VideoPlayer
                src={activeVideo?.videoUrl ?? ''}
                onEnded={handleVideoEnded}
                onWatchProgress={handleWatchProgress}
              />

              {activeVideo && (
                <p className="text-body-sm text-on-surface-variant -mt-2">
                  Now playing: <span className="text-on-surface font-medium">{activeVideo.title}</span>
                </p>
              )}

              <div className="flex flex-col md:flex-row justify-between items-start gap-4">
                <div className="min-w-0">
                  <h1 className="font-headline-lg text-headline-sm font-semibold text-on-surface mb-2">
                    {lesson.title}
                  </h1>
                  <p className="text-on-surface-variant text-body-sm leading-relaxed">
                    {lesson.description}
                  </p>
                </div>

                <div className="relative group flex-shrink-0">
                  <button
                    type="button"
                    onClick={handleMarkAsComplete}
                    disabled={isLessonCompleted || isCompleting || !canMarkComplete}
                    className={`px-6 py-3 rounded-[4px] font-button text-button uppercase tracking-wider font-bold transition-all flex items-center gap-2 ${
                      isLessonCompleted
                        ? 'bg-transparent border border-white/10 text-on-surface-variant cursor-default'
                        : canMarkComplete
                          ? 'bg-[#e03c3c] hover:brightness-110 active:scale-[0.97] text-white cursor-pointer'
                          : 'bg-surface-variant text-on-surface-variant cursor-not-allowed opacity-60'
                    }`}
                  >
                    {isLessonCompleted ? (
                      <>
                        Completed
                        <span
                          className="material-symbols-outlined text-[18px] text-green-500 font-bold"
                          style={{ fontVariationSettings: "'FILL' 1" }}
                        >
                          check
                        </span>
                      </>
                    ) : (
                      <>
                        {isCompleting ? 'Processing...' : 'Mark as Complete'}
                        <span className="material-symbols-outlined text-[18px]">check_circle</span>
                      </>
                    )}
                  </button>
                  {!canMarkComplete && !isLessonCompleted && (
                    <div className="pointer-events-none absolute bottom-full right-0 mb-2 w-64 opacity-0 group-hover:opacity-100 transition-opacity z-20">
                      <div className="bg-[#1a1a1a] border border-outline-variant text-on-surface-variant text-xs px-3 py-2 rounded shadow-lg text-center">
                        {!allVideosAt90 && requiresChallenge && !challengePassed
                          ? 'Watch all videos to 90% and complete the challenge to unlock'
                          : !allVideosAt90
                            ? 'Watch all videos to at least 90% to unlock'
                            : requiresChallenge && !challengePassed
                              ? 'Complete the challenge below to unlock'
                              : ''}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-[#141414] thin-border rounded-lg p-gutter">
                <h3 className="font-label-caps text-label-caps text-on-surface-variant uppercase mb-3 tracking-widest">
                  Instructor Notes
                </h3>
                <div className="text-on-surface-variant text-body-sm leading-relaxed whitespace-pre-wrap">
                  {lesson.notes ||
                    'Focus on target crosshairs and keep spacing constant. Jump during vertical momentum peaks for critical multipliers.'}
                </div>
              </div>

              {/* Interactive Challenge Section */}
              {challengeHtml && (
                <div className="bg-[#141414] thin-border rounded-lg overflow-hidden">
                  <div className="p-4 border-b border-outline-variant/30 flex items-center justify-between">
                    <h3 className="font-label-caps text-label-caps text-on-surface-variant uppercase tracking-widest">
                      Interactive Challenge
                    </h3>
                    {requiresChallenge && (
                      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-[4px] text-[11px] font-bold uppercase tracking-wider ${
                        challengePassed
                          ? 'bg-green-500/15 text-green-500 border border-green-500/30'
                          : 'bg-amber-500/15 text-amber-500 border border-amber-500/30'
                      }`}>
                        <span
                          className="material-symbols-outlined text-[16px]"
                          style={{ fontVariationSettings: "'FILL' 1" }}
                        >
                          {challengePassed ? 'check_circle' : 'lock'}
                        </span>
                        {challengePassed ? 'Challenge Passed' : 'Required to Complete'}
                      </div>
                    )}
                  </div>

                  {/* Challenge iframe with postMessage protocol */}
                  <iframe
                    title="Challenge"
                    srcDoc={challengeHtml}
                    className="w-full h-[700px] border-0"
                    sandbox="allow-scripts allow-same-origin"
                  />

                  {/* Manual challenge-pass button as fallback (if challenge HTML doesn't use postMessage) */}
                  {requiresChallenge && !challengePassed && (
                    <div className="p-4 border-t border-outline-variant/30 bg-surface-container">
                      <div className="flex items-center justify-between">
                        <p className="text-on-surface-variant text-body-sm">
                          When you finish the challenge, click below to mark it as passed.
                        </p>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              await api.post(`/api/lessons/${lessonId}/challenge-pass`);
                              setChallengePassed(true);
                              toast.push({ type: 'success', message: 'Challenge passed!' });
                            } catch {
                              toast.push({ type: 'error', message: 'Failed to mark challenge as passed.' });
                            }
                          }}
                          className="px-4 py-2 rounded-[4px] bg-[#e03c3c] hover:brightness-110 active:scale-95 text-white font-button text-button uppercase tracking-wider font-bold transition-all flex items-center gap-2"
                        >
                          <span className="material-symbols-outlined text-[16px]">check</span>
                          I Completed the Challenge
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Course lessons — switch between lessons in this course */}
              <div className="bg-[#141414] thin-border rounded-lg overflow-hidden">
                <div className="p-4 border-b border-outline-variant/30">
                  <h3 className="font-label-caps text-label-caps text-on-surface-variant uppercase tracking-widest">
                    Other lessons in this course
                  </h3>
                </div>
                <div className="divide-y divide-outline-variant/30 max-h-[220px] overflow-y-auto">
                  {lessonsList.map((item, idx) => {
                    const isSelected = item.id === lessonId;
                    const isDone = completedLessons.has(item.id);
                    const totalSec = (item.videos || []).reduce(
                      (s: number, v: { durationSeconds: number }) => s + v.durationSeconds,
                      0
                    );
                    return (
                      <div
                        key={item.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => navigate(`/courses/${courseId}/lessons/${item.id}`)}
                        onKeyDown={(e) =>
                          e.key === 'Enter' && navigate(`/courses/${courseId}/lessons/${item.id}`)
                        }
                        className={`flex items-center justify-between p-4 cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-surface-variant border-l-2 border-error'
                            : 'hover:bg-surface-container-high'
                        }`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span
                            className={`font-mono text-body-sm ${
                              isSelected ? 'text-[#e03c3c] font-bold' : 'text-outline'
                            }`}
                          >
                            {String(idx + 1).padStart(2, '0')}
                          </span>
                          <div className="min-w-0">
                            <div
                              className={`text-body-sm truncate ${
                                isSelected ? 'text-on-surface font-medium' : 'text-on-surface-variant'
                              }`}
                            >
                              {item.title}
                            </div>
                            <div className="text-[11px] text-outline font-mono">
                              {formatDuration(totalSec)}
                            </div>
                          </div>
                        </div>
                        {isDone && (
                          <span
                            className="material-symbols-outlined text-green-500 text-[18px] flex-shrink-0"
                            style={{ fontVariationSettings: "'FILL' 1" }}
                          >
                            check
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Video playlist — right sidebar */}
        <div className="col-span-12 lg:col-span-4">
          <div className="bg-[#0d0d0d] thin-border rounded-lg overflow-hidden flex flex-col lg:sticky lg:top-4 max-h-[calc(100vh-120px)]">
            <div className="p-4 border-b border-outline-variant/30 bg-surface-container">
              <h2 className="font-label-caps text-label-caps text-on-surface-variant uppercase tracking-widest mb-1">
                Lesson playlist
              </h2>
              <p className="text-[11px] text-on-surface-variant">
                {sortedVideos.filter((v) => (videoProgress[v.id] ?? 0) >= 90).length} /{' '}
                {sortedVideos.length} videos at 90%+
              </p>
            </div>

            <div className="flex-1 overflow-y-auto divide-y divide-outline-variant/20">
              {sortedVideos.length === 0 ? (
                <p className="p-4 text-on-surface-variant text-body-sm">No videos in this lesson yet.</p>
              ) : (
                sortedVideos.map((video) => {
                  const pct = videoProgress[video.id] ?? 0;
                  const isActive = video.id === activeVideoId;
                  return (
                    <button
                      key={video.id}
                      type="button"
                      onClick={() => selectVideo(video.id)}
                      className={`w-full text-left flex items-center gap-3 p-4 transition-colors ${
                        isActive
                          ? 'bg-surface-variant border-l-2 border-error'
                          : 'hover:bg-surface-container-high'
                      }`}
                    >
                      <VideoStatusIcon watchPercentage={pct} />
                      <div className="min-w-0 flex-1">
                        <div
                          className={`text-body-sm truncate ${
                            isActive ? 'text-on-surface font-semibold' : 'text-on-surface-variant'
                          }`}
                        >
                          {video.title}
                        </div>
                        <div className="text-[11px] font-mono text-outline mt-0.5">
                          {formatDuration(video.durationSeconds)}
                          {pct > 0 && pct < 90 && (
                            <span className="text-amber-400/90 ml-2">{pct}%</span>
                          )}
                        </div>
                      </div>
                      {isActive && (
                        <span className="material-symbols-outlined text-error text-[20px] flex-shrink-0">
                          play_circle
                        </span>
                      )}
                    </button>
                  );
                })
              )}
            </div>

            {isLessonCompleted && (
              <div className="p-3 border-t border-green-500/30 bg-green-500/10 flex items-center gap-2 text-green-500 text-sm font-medium">
                <span
                  className="material-symbols-outlined text-[18px]"
                  style={{ fontVariationSettings: "'FILL' 1" }}
                >
                  check_circle
                </span>
                Lesson completed
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
